
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { MessageCircle, Calendar, BarChart, Users, Camera, Zap, Clock, Award } from "lucide-react";

const SocialMediaPage = () => {
  useEffect(() => {
    document.title = "Social Media Management - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <MessageCircle className="w-8 h-8 text-blue-600" />,
      title: "Content Creation",
      description: "Engaging posts, stories, and multimedia content tailored for each social platform"
    },
    {
      icon: <Calendar className="w-8 h-8 text-blue-600" />,
      title: "Content Scheduling",
      description: "Strategic posting schedules to maximize reach and engagement across all platforms"
    },
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Community Management",
      description: "Active engagement with your audience, responding to comments and messages promptly"
    },
    {
      icon: <BarChart className="w-8 h-8 text-blue-600" />,
      title: "Analytics & Reporting",
      description: "Detailed insights and performance reports to track growth and optimize strategies"
    },
    {
      icon: <Camera className="w-8 h-8 text-blue-600" />,
      title: "Visual Content Design",
      description: "Eye-catching graphics, videos, and visual content that stands out in feeds"
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Social Media Strategy",
      description: "Comprehensive strategies to build brand awareness and drive business growth"
    }
  ];

  const platforms = [
    { name: "Facebook", icon: "👥" },
    { name: "Instagram", icon: "📸" },
    { name: "TikTok", icon: "🎵" },
    { name: "LinkedIn", icon: "💼" },
    { name: "Twitter", icon: "🐦" },
    { name: "YouTube", icon: "📺" }
  ];

  const services_offered = [
    "Content Calendar Planning",
    "Hashtag Research & Strategy",
    "Social Media Advertising",
    "Influencer Collaborations",
    "Crisis Management",
    "Brand Voice Development",
    "Competitor Analysis",
    "Growth Hacking Strategies"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Professional social media management services in Nepal. Content creation, community management, scheduling, and analytics." />
        <meta name="keywords" content="social media management, content creation, community management, social media strategy, Nepal" />
        <meta property="og:title" content="Social Media Management - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              Social Media Management
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Build, engage, and grow your social media presence with our comprehensive management services.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Platforms Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Platforms We Manage</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {platforms.map((platform, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{platform.icon}</div>
                  <h3 className="font-semibold">{platform.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Services Offered Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services_offered.map((service, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="font-medium">{service}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Management Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Management Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Audit", desc: "Analyze current social media presence" },
                { step: "02", title: "Strategy", desc: "Develop customized content strategy" },
                { step: "03", title: "Execute", desc: "Create and publish engaging content" },
                { step: "04", title: "Optimize", desc: "Monitor performance and optimize" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Boost Your Social Presence?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Let's grow your social media following and engagement together.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default SocialMediaPage;
